export type SubscriptionTier = 'free' | 'plus' | 'employer' | 'employer_plus' | string | null | undefined;

export type UserAccessProfile = {
  userType?: 'talent' | 'employer' | string | null;
  subscription?: SubscriptionTier;
  messagingAccess?: boolean | null;
  verificationStatus?: 'approved' | 'pending' | 'rejected' | string | null;
  isAdmin?: boolean | null;
};

const UNAUTHENTICATED_ALLOWED_VIEWS = new Set([
  'home',
  'profile',
  'auth',
  'terms',
  'privacy',
  'faq',
  'forgot-password',
  'gigs-coming',
  'subscription-coming',
]);

export function isUnauthenticatedViewAllowed(view: string): boolean {
  return UNAUTHENTICATED_ALLOWED_VIEWS.has(view);
}

export function canCreateGig(profile: UserAccessProfile | null | undefined): boolean {
  return profile?.subscription === 'employer_plus';
}

export function canUseFavorites(profile: UserAccessProfile | null | undefined): boolean {
  const subscription = profile?.subscription;
  return subscription === 'employer' || subscription === 'employer_plus';
}

export function canUseSavedSearches(profile: UserAccessProfile | null | undefined): boolean {
  return profile?.subscription === 'employer_plus';
}

export function canShowGigInterest(profile: UserAccessProfile | null | undefined): boolean {
  const subscription = profile?.subscription;
  return subscription === 'plus' || subscription === 'employer' || subscription === 'employer_plus';
}

export function canUploadVideo(profile: UserAccessProfile | null | undefined): boolean {
  return profile?.subscription === 'plus';
}

export function isPremiumSubscription(subscription: SubscriptionTier): boolean {
  return subscription === 'plus' || subscription === 'employer' || subscription === 'employer_plus';
}

export function getSubscriptionDisplayName(subscription: SubscriptionTier): string {
  if (subscription === 'free') return 'Gratis';
  if (subscription === 'plus') return 'Plus';
  if (subscription === 'employer') return 'Employer';
  if (subscription === 'employer_plus') return 'Employer Plus';
  return 'Ukjent';
}

export function hasEmployerMessagingAccess(profile: UserAccessProfile | null | undefined): boolean {
  if (!profile) return false;
  return profile.messagingAccess === true || profile.subscription === 'employer' || profile.subscription === 'employer_plus';
}

export function canTalentStartConversation(profile: UserAccessProfile | null | undefined): boolean {
  if (!profile) return false;
  return profile.userType === 'talent' && profile.subscription === 'plus';
}

export function canStartConversation(profile: UserAccessProfile | null | undefined): boolean {
  if (!profile) return false;
  if (profile.isAdmin === true) return true;
  if (canTalentStartConversation(profile)) return true;
  if (profile.userType === 'employer') return hasEmployerMessagingAccess(profile);
  return false;
}

export function canReplyInExistingConversation(profile: UserAccessProfile | null | undefined): boolean {
  if (!profile) return false;
  if (profile.isAdmin === true) return true;
  // Free talents can reply in existing conversations by policy.
  if (profile.userType === 'talent') return true;
  // Employers can reply in existing conversations even without messaging access.
  if (profile.userType === 'employer') return true;
  return false;
}

export function canCreateTalentPackage(profile: UserAccessProfile | null | undefined): boolean {
  if (!profile) return false;
  return profile.userType === 'talent' && profile.subscription === 'plus';
}

export function canSendTalentPackageRequest(profile: UserAccessProfile | null | undefined): boolean {
  return canStartConversation(profile);
}

export function isEmployerApproved(profile: UserAccessProfile | null | undefined): boolean {
  if (!profile) return false;
  if (profile.userType !== 'employer') return true;
  return profile.verificationStatus === 'approved';
}

export function canPurchaseEmployerSubscription(
  profile: UserAccessProfile | null | undefined,
  currentPlan?: string | null
): boolean {
  if (profile?.userType !== 'employer') return true;
  if (currentPlan === 'employer_plus') return true;
  return isEmployerApproved(profile);
}
